import React, { useEffect, useState } from "react"
import { useHookstate, type State, type StateMethods } from "@hookstate/core";
import type { IFilters } from "~/components/ListDispatchesSocket/services";
import { useLang } from "~/hooks/useLang";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, List, ListItem, ListItemText, Typography } from "@mui/material";
import { useTheme } from "~/hooks/useTheme";
import { LoadingButton } from "@mui/lab";
import { getDispatchGroup } from "~/components/FilterAutocompleteDispatchGroup/service";
import type { DispatchGroupResponse } from "~/components/FilterAutocompleteDispatchGroup/interface";
import FilterAutocompleteDispatchGroup from "~/components/FilterAutocompleteDispatchGroup";
import { Close } from "@mui/icons-material";
import notify from "~/utils/notify";
import useListVehiclesSocket from "~/components/ListVehiclesSocket/stores/ListVehiclesSocketState";
import { transferMultipleDevices } from "../../services";
import useListDevicesSocket from "../../stores/ListDevicesSocketState";
import type { IListDeviceSocket } from "../../interfaces/IListDevicesprops";
import type { GridInputRowSelectionModel } from "@mui/x-data-grid";

interface DialogTransferMultipleDevicesProps {
  readonly openDialog: State<boolean>;
  readonly onClose: any;
  readonly rowsSelected: State<any>;
  readonly enableButtonShowResume: State<boolean>;
  readonly openModalResultMultipleTransfertVehicle: StateMethods<boolean>;
  readonly clearFilters: () => void;
  // readonly handleLoadDevices: (filters?: readonly IFilters[]) => void;
}

// eslint-disable-next-line max-lines-per-function
export default function DialogTransferMultipleDevices({
  onClose,
  openDialog,
  rowsSelected,
  enableButtonShowResume,
  openModalResultMultipleTransfertVehicle,
  clearFilters }: DialogTransferMultipleDevicesProps) {
  const { translate } = useLang();
  const { darkMode } = useTheme();
  const { listDevicesMultipleTransfer } = useListDevicesSocket();
  const dispatchGroupId = useHookstate<number | null>(null);
  const openDialogErrorClosed = useHookstate(false);
  const loading = useHookstate(false);
  const successTransfer = listDevicesMultipleTransfer().get().filter((element) => element.error === '').map(item => item.error).length
  const errorTransfer = listDevicesMultipleTransfer().get().filter((element) => element.error != '').map(item => item.error).length

  useEffect(() => {
    if (!rowsSelected.get().length) {
      openDialog.set(false);
    }
  }, [rowsSelected.get().length])

  const handleClose = () => {
    dispatchGroupId.set(null)
    openDialog.set(false);
  };

  const handleTransferMultipleDevices = (params) => {
    const ids = params.map(row => row.id)

    if (dispatchGroupId.get()) {
      transferMultipleDevices({ devicesIds: ids, dispatchGroup: dispatchGroupId.get() })
        .then((response) => {
          if (response.status === 200) {
            notify({
              message: `${translate('The batch transfer will be processed and you will receive a notice when the process is complete')}`,
              type: 'info'
            })
            onClose
          }
        })
        .finally(() => {
          openDialog.set(false)
        })
    }
  }

  const clickOutModalErroClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      openDialogErrorClosed.set(false);
    }
  };

  const handleCLoseModalError = () => {
    openDialogErrorClosed.set(false)
    openModalResultMultipleTransfertVehicle.set(false)
    enableButtonShowResume.set(false)
    listDevicesMultipleTransfer().set([])
  }

  const removeDeviceList = (id) => {
    const listUpdated: IListDeviceSocket[] = JSON.parse(JSON.stringify(rowsSelected.get().filter((item) => item.id != id)))
    rowsSelected.set(listUpdated)
  }

  return (
    <>
      <Dialog
        open={openDialog.get()}
        onClose={onClose}
        disableEscapeKeyDown={true}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>
          {translate('Transfer devices')}
        </DialogTitle>
        <DialogContent>
          <Typography variant='subtitle1'>
            {translate('Summary of the selected devices')}
          </Typography>
          {rowsSelected?.get().length ?
            <List
              sx={{
                width: '100%',
                maxHeight: '300px',
                bgcolor: darkMode ? 'transparent' : '#FFF',
                marginY: 2,
                overflowY: 'auto',
                border: 1,
                borderColor: 'grey.500',
                borderRadius: 1
              }}
            >
              {rowsSelected?.get().map((item) => {
                return (
                  <>
                    <ListItem key={item?.id}>
                      <ListItemText
                        sx={{ display: 'inline', maxHeight: '150px', overflow: 'auto', whiteSpace: 'pre-line' }}
                        primary={`${translate('Name')}: ${item?.identification} `}
                        secondary={
                          <Typography>
                            {`${translate('Dispatch Group')}: ${item?.dispatchGroup}`}
                          </Typography>} />
                      <IconButton
                        size="small"
                        onClick={() => removeDeviceList(item.id)}
                      >
                        <Close />
                      </IconButton>
                    </ListItem>
                    <Divider
                      component="li"
                      sx={{
                        "&:last-child": {
                          display: 'none',
                        }
                      }} />
                  </>
                )
              })}
            </List> :

            <Typography marginY={2} color={'red'}>
              {translate('Nenhum Device Foi selecionado')}
            </Typography>
          }
          <FilterAutocompleteDispatchGroup
            dispatchGroupId={dispatchGroupId.get() ?? undefined}
            onGroupDispatchChange={(disptachGroupValue) => {
              dispatchGroupId.set(disptachGroupValue);
            }}
            sizeDropDown={200}
            disabled={!rowsSelected.get().length}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={handleClose}>
            {translate('Cancel')}
          </Button>
          <LoadingButton
            loading={loading.get()}
            disabled={!rowsSelected.get().length}
            variant='contained'
            onClick={() => {
              handleTransferMultipleDevices(rowsSelected.get())
            }}>
            {translate('Submit')}
          </LoadingButton>

        </DialogActions>
      </Dialog>
      <Dialog
        open={openModalResultMultipleTransfertVehicle.get()}
        onClose={clickOutModalErroClose}
        disableEscapeKeyDown={true}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
          {translate('Summary of last batch devices transfer')}
          <IconButton
            aria-label="close"
            onClick={() => handleCLoseModalError()}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <List
            sx={{
              width: '100%',
              maxHeight: '500px',
              bgcolor: darkMode ? 'transparent' : '#FFF',
              marginTop: 2,
              overflowY: 'auto',
              border: 1,
              borderColor: 'grey.500',
              borderRadius: 1
            }}
          >
            {listDevicesMultipleTransfer().get()?.map((item, index) => (
              <>
                <ListItem key={index}>
                  <ListItemText
                    sx={{ display: 'inline', maxHeight: '150px', overflow: 'auto', whiteSpace: 'pre-line' }}
                    primary={
                      <Typography>
                        {`${translate('Device:name')}: ${item?.device ? item?.device : translate('Not informed')}`}
                      </Typography>
                    }
                    secondary={
                      item?.error?.length ?
                        <Typography variant="body2" color='error'>
                          {`${translate('Error')}: ${translate(item?.error)}`}
                        </Typography> :
                        <></>
                    }
                  />
                </ListItem >
                <Divider
                  component="li"
                  sx={{
                    "&:last-child": {
                      display: 'none',
                    }
                  }} />
              </>
            ))}
          </List>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openModalResultMultipleTransfertVehicle.get()}
        onClose={clickOutModalErroClose}
        disableEscapeKeyDown={true}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
          {translate('Summary of last batch device transfer')}
          <IconButton
            aria-label="close"
            onClick={() => handleCLoseModalError()}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {successTransfer > 0 ? translate('Devices transferred with success', { number: successTransfer }) : translate('No devices has been transfer')}
          </Typography>
          {successTransfer > 0 &&
            <List
              sx={{
                width: '100%',
                maxHeight: '500px',
                bgcolor: darkMode ? 'transparent' : '#FFF',
                marginTop: 2,
                overflowY: 'auto',
                border: 1,
                borderColor: 'grey.500',
                borderRadius: 1,
              }}
            >
              {listDevicesMultipleTransfer().get()?.filter((element) => element.error === '').map((item, index, { length }) => (
                <>
                  <ListItem key={index} >
                    <ListItemText
                      sx={{
                        display: 'inline',
                        maxHeight: '150px',
                        overflow: 'auto',
                        whiteSpace: 'pre-line',
                      }}
                      primary={
                        <Typography>
                          {`${translate('Device:name')}: ${item.device ? item.device : translate('Not informed')}`}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Divider
                    sx={{
                      "&:last-child": {
                        display: 'none',
                      }
                    }} />
                </>
              ))}
            </List>
          }

          <Typography variant="body1" marginTop={2}>
            {errorTransfer > 0 ? translate('One or more errors occurred while transferring devices', { number: errorTransfer }) : translate('There were no transfer errors')}
          </Typography>
          {errorTransfer > 0 &&
            <List
              sx={{
                width: '100%',
                maxHeight: '500px',
                bgcolor: darkMode ? 'transparent' : '#FFF',
                marginTop: 2,
                overflowY: 'auto',
                border: 1,
                borderColor: 'grey.500',
                borderRadius: 1
              }}
            >
              {listDevicesMultipleTransfer().get()
                .filter((element) => element.error ? element.error.length > 0 : '')
                .map((item, index) => {
                  return (
                    <>
                      {item &&
                        <ListItem key={index}>
                          <ListItemText
                            sx={{ display: 'inline', maxHeight: '150px', overflow: 'auto', whiteSpace: 'pre-line' }}
                            primary={`${translate('Device:name')}: ${item.device ? item.device : translate('Not informed')}`}
                            secondary={
                              <Typography variant="body2" color='error'>
                                {`${translate('Error')}: ${translate(item.error ? item.error : '')}`}
                              </Typography>
                            }
                          />
                          <Divider />
                        </ListItem >
                      }
                      <Divider />
                    </>
                  )
                })}
            </List>
          }
        </DialogContent>
      </Dialog >
    </>
  )
}